import axios from "axios";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";
import { IPhoneOrder, IPhoneOrderProducts } from "../types/phoneOrder.type";
import { formatDate } from "./formatDate";

// Завантажуємо шаблон Excel
const fetchGoogleSheetTemplate = async (url: string): Promise<ArrayBuffer> => {
  const response = await axios.get(url, { responseType: "arraybuffer" });
  return response.data;
};

const getUnitPrice = (
  orderProduct: IPhoneOrderProducts,
  isRetail: boolean
): number => {
  if (
    orderProduct.pricePerUnit != null &&
    !isNaN(Number(orderProduct.pricePerUnit))
  ) {
    return Number(orderProduct.pricePerUnit);
  }

  return Number(
    isRetail
      ? orderProduct.product.price
      : orderProduct.product.wholeSalePrice
  );
};

export const createInvoice = async (dataObject: IPhoneOrder) => {
  const isRetail = dataObject.type === "retail";
  try {
    // Завантажуємо шаблон
    const templateBuffer = await fetchGoogleSheetTemplate(
      "https://docs.google.com/spreadsheets/d/1wRyT1EPH99qEggybCW-cwzTouGui4tbP/export?format=xlsx"
    );

    // Створюємо нову книгу з шаблону
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(templateBuffer);

    // Отримуємо перший аркуш
    const worksheet = workbook.worksheets[0];

    // Вставляємо статичні дані
    worksheet.getCell("B3").value = dataObject.invoice || "";
    worksheet.getCell("A4").value = dataObject.client.fullName || "";
    worksheet.getCell("C4").value =
      formatDate(dataObject.createdAt) || new Date().toLocaleDateString();
    worksheet.getCell("A5").value =
      dataObject.adressLine1 + " " + dataObject.adressLine2 || "";

    // Рядок, де починаються товари
    const startRow = 7;

    // Заміщуємо 7-й рядок першим продуктом
    if (dataObject.orderProducts.length > 0) {
      const firstProduct = dataObject.orderProducts[0];
      const templateRow = worksheet.getRow(startRow); // Рядок-шаблон
      const firstRow = worksheet.getRow(startRow);
      const unitPrice = getUnitPrice(firstProduct, isRetail);

      // Заповнюємо дані
      firstRow.getCell(1).value = firstProduct.product.name; // Назва продукту
      firstRow.getCell(2).value = firstProduct.product.capacity; // Опис
      firstRow.getCell(3).value = firstProduct.quantity; // Кількість
      firstRow.getCell(4).value = unitPrice; // Ціна за одиницю
      firstRow.getCell(5).value = firstProduct.quantity * unitPrice; // Сума

      // Копіюємо стилі з шаблонного рядка
      firstRow.height = templateRow.height; // Висота рядка
      firstRow.eachCell((cell, colNumber) => {
        const templateCell = templateRow.getCell(colNumber);
        cell.style = { ...templateCell.style }; // Копіюємо стиль
        if (templateCell.border) cell.border = { ...templateCell.border }; // Копіюємо межі
        if (templateCell.alignment)
          cell.alignment = { ...templateCell.alignment }; // Копіюємо вирівнювання
      });

      firstRow.commit();
    }

    // Додаємо решту продуктів після 7-го рядка
    dataObject.orderProducts.slice(1).forEach((product, index) => {
      const rowIndex = startRow + index + 1; // Додаємо після 7-го рядка
      const unitPrice = getUnitPrice(product, isRetail);

      // Вставляємо новий рядок
      worksheet.spliceRows(rowIndex, 0, [
        product.product.name, // Назва продукту
        product.product.capacity, // Опис
        product.quantity, // Кількість
        unitPrice, // Ціна за одиницю
        product.quantity * unitPrice, // Сума
      ]);

      // Копіюємо стилі з рядка-шаблону
      const templateRow = worksheet.getRow(startRow);
      const newRow = worksheet.getRow(rowIndex);

      newRow.height = templateRow.height; // Висота рядка
      newRow.eachCell((cell, colNumber) => {
        const templateCell = templateRow.getCell(colNumber);
        cell.style = { ...templateCell.style }; // Копіюємо стиль
        if (templateCell.border) cell.border = { ...templateCell.border }; // Копіюємо межі
        if (templateCell.alignment)
          cell.alignment = { ...templateCell.alignment }; // Копіюємо вирівнювання
      });
    });

    const subTotalRow = startRow + dataObject.orderProducts.length;
    const subTotal =
      dataObject.customPrice != null && !isNaN(Number(dataObject.customPrice))
        ? Number(dataObject.customPrice)
        : dataObject.orderProducts.reduce(
            (acc, item) =>
              (acc += item.quantity * getUnitPrice(item, isRetail)),
            0
          );
    worksheet.unMergeCells(`A${subTotalRow}:C${subTotalRow}`);
    worksheet.mergeCells(`A${subTotalRow}:C${subTotalRow}`);

    worksheet.getCell(`E${subTotalRow}`).value = subTotal;
    const tax = subTotal * 0;
    // worksheet.unMergeCells(`A${subTotalRow + 1}:B${subTotalRow + 1}`);
    // worksheet.mergeCells(`A${subTotalRow}:B${subTotalRow}`);
    worksheet.getCell(`E${subTotalRow + 1}`).value = tax;

    const total = tax + subTotal;
    worksheet.getCell(`E${subTotalRow + 2}`).value = total;
    worksheet.unMergeCells(`A${subTotalRow + 3}:C${subTotalRow + 3}`);
    worksheet.mergeCells(`A${subTotalRow + 3}:C${subTotalRow + 3}`);
    worksheet.unMergeCells(`A${subTotalRow + 4}:C${subTotalRow + 4}`);
    worksheet.mergeCells(`A${subTotalRow + 4}:E${subTotalRow + 4}`);
    worksheet.unMergeCells(`A${subTotalRow + 5}:E${subTotalRow + 5}`);
    worksheet.mergeCells(`A${subTotalRow + 5}:E${subTotalRow + 5}`);

    // Зберігаємо файл
    const buffer = await workbook.xlsx.writeBuffer();
    const blob = new Blob([buffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(
      blob,
      `${formatDate(dataObject.createdAt)} ${dataObject.client.fullName}.xlsx`
    );
  } catch (error) {
    console.error("Error creating invoice:", error);
  }
};
